import { type FC, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import { Dialog } from '@mui/material'
import { type Theme, useTheme } from '@mui/material/styles'

import VisuallyHiddenLabel from '@DS/components/components/utils/aria/VisuallyHiddenLabel'
import ConditionalWrapper from '@DS/components/components/utils/wrapper/ConditionalWrapper'
import Modal from '@DS/components/navigation/modal/Modal'
import ModalPage from '@DS/components/navigation/modal/ModalPage'
import { type UseModalStateReturn } from '@Hooks/useModal'
import { useIsLargeUp } from '@Hooks/useResponsive'

import JourneyDatePickerForm, { type JourneyDatePickerFormProps } from './JourneyDatePickerForm'
import * as styles from './styles'

export type JourneyDatePickerWrapperProps = JourneyDatePickerFormProps & {
  datePickerOverrideTheme?: Theme
  modalProps: UseModalStateReturn['modalProps']
  visuallyHiddenLabelId?: string
  isInModal?: boolean
}

const JourneyDatePickerWrapper: FC<JourneyDatePickerWrapperProps> = ({
  modalProps,
  visuallyHiddenLabelId,
  mode,
  isInModal = false,
  ...datepickerFormProps
}) => {
  const { formatMessage } = useIntl()
  const theme = useTheme()

  const isLargeUp = useIsLargeUp(theme)

  const dialogRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    dialogRef.current?.focus()
  }, [])

  const getDisabilityText = () => {
    if (visuallyHiddenLabelId) {
      return visuallyHiddenLabelId
    }

    switch (mode) {
      case 'INWARD':
        return 'datepicker_inward_visuallyHiddenLabel'
      case 'OUTWARD':
      case 'OUTWARD_ONLY':
      case 'SINGLE_START_DATE':
      case 'SINGLE_START_DATE_WITH_HOURS':
        return 'datepicker_outward_visuallyHiddenLabel'
      default:
        return 'datepicker_roundTrip_visuallyHiddenLabel'
    }
  }

  return (
    <ConditionalWrapper
      condition={isLargeUp && !isInModal}
      wrapper={(children) => (
        <Dialog
          fullWidth
          open={modalProps.isOpen}
          onClose={modalProps.onClose}
          aria-labelledby="journeyDatePickerWrapperDescriptionLabel"
          slotProps={{
            paper: { style: styles.journeyDatePickerDialog },
          }}
        >
          <div ref={dialogRef} tabIndex={-1}>
            {children}
          </div>
        </Dialog>
      )}
      defaultWrapper={(children) => (
        <Modal {...modalProps}>
          <ModalPage headerTitle={formatMessage({ id: 'datepicker_mobile_title' })}>{children}</ModalPage>
        </Modal>
      )}
    >
      <VisuallyHiddenLabel
        id="journeyDatePickerWrapperDescriptionLabel"
        label={formatMessage({ id: getDisabilityText() })}
      />
      <JourneyDatePickerForm mode={mode} {...datepickerFormProps} />
    </ConditionalWrapper>
  )
}

export default JourneyDatePickerWrapper
